import type { CSSProperties, ReactNode } from "react";
import { cn } from "../utils/cx.js";

export interface DescriptionItem {
  key?: string;
  label: ReactNode;
  children: ReactNode;
  span?: number;
}

export interface DescriptionsProps {
  items: DescriptionItem[];
  title?: ReactNode;
  extra?: ReactNode;
  column?: number;
  bordered?: boolean;
  size?: "default" | "middle" | "small";
  layout?: "horizontal" | "vertical";
  className?: string;
}

export function Descriptions({
  items,
  title,
  extra,
  column = 3,
  bordered,
  size = "default",
  layout = "horizontal",
  className,
}: DescriptionsProps) {
  const pad = size === "small" ? "px-3 py-2" : size === "middle" ? "px-3.5 py-2.5" : "px-4 py-3";

  return (
    <div className={className}>
      {(title != null || extra != null) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          {title != null && <div className="text-base font-semibold">{title}</div>}
          {extra != null && <div>{extra}</div>}
        </div>
      )}
      <dl
        className={cn("grid gap-x-6 gap-y-3", bordered && "gap-0 overflow-hidden rounded-lg border")}
        style={{ gridTemplateColumns: `repeat(${column}, minmax(0, 1fr))` }}
      >
        {items.map((it, i) => {
          const cellStyle: CSSProperties | undefined = it.span
            ? { gridColumn: `span ${it.span}` }
            : undefined;
          if (layout === "vertical") {
            return (
              <div key={it.key ?? i} style={cellStyle} className={cn(bordered && `border-r border-b ${pad}`)}>
                <dt className="mb-1 text-xs font-medium text-muted-foreground">{it.label}</dt>
                <dd className="text-sm">{it.children}</dd>
              </div>
            );
          }
          return (
            <div
              key={it.key ?? i}
              style={cellStyle}
              className={cn("flex", bordered ? "border-r border-b" : "flex-col gap-0.5")}
            >
              <dt
                className={cn(
                  "text-xs font-medium text-muted-foreground",
                  bordered && `w-2/5 shrink-0 bg-muted/40 ${pad}`,
                )}
              >
                {it.label}
              </dt>
              <dd className={cn("text-sm", bordered && `flex-1 ${pad}`)}>{it.children}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
